import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";

import { getAuthToken } from "services/AccountServices";
import { mcpClientService } from "mcp/services/MCPClientService";
import { useMCP } from "mcp/hooks/useMCP";

interface TokenValidationResult {
    success: boolean;
    error: string | null;
    message: string;
    user: {
        email: string;
        id: string;
        first_name: string;
        last_name: string;
    } | null;
}

export interface UseMCPAuthResult {
    authenticatedUserEmail: string | null;
    getAuthStatus: () => Promise<{ authenticated: boolean; email?: string; user?: TokenValidationResult["user"] } | null>;
    hasAuthToken: boolean;
    isCindraAuthenticated: boolean;
    isCindraEnabled: boolean;
    tokenValidated: boolean | null;
    validateToken: () => Promise<TokenValidationResult | null>;
    validatedUser: TokenValidationResult["user"] | null;
}

const MCPAuthContext = createContext<UseMCPAuthResult | null>(null);

export const MCPAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [authenticatedUserEmail, setAuthenticatedUserEmail] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [tokenValidated, setTokenValidated] = useState<boolean | null>(null);
    const [validatedUser, setValidatedUser] = useState<TokenValidationResult["user"] | null>(null);
    const { isCindraConnected, error, isCindraEnabled } = useMCP();

    // Use refs to track validation state and prevent excessive calls
    const lastValidationTime = useRef<number>(0);
    const validationCooldown = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mounted = useRef(true);

    // Minimum time between validation calls (5 seconds)
    const VALIDATION_THROTTLE_MS = 5000;

    // Stable functions using useCallback with proper dependencies
    const hasAuthToken = useCallback(() => {
        const token = getAuthToken();
        return token !== undefined && token !== null && token.trim() !== "";
    }, []);

    const getCurrentUserEmail = useCallback(() => {
        try {
            const user = document.cookie
                .split("; ")
                .find(row => row.startsWith("user="))
                ?.split("=")[1];

            if (user) {
                const userData = JSON.parse(decodeURIComponent(user));
                return userData?.email || null;
            }
        }
        catch {
            return null;
        }
        return null;
    }, []);

    const validateToken = useCallback(async (): Promise<TokenValidationResult | null> => {
        if (!mounted.current || !isCindraEnabled || !isCindraConnected || !hasAuthToken() || isValidating) {
            return null;
        }

        // Throttle validation calls
        const now = Date.now();
        if (now - lastValidationTime.current < VALIDATION_THROTTLE_MS) {
            console.log("[useMCPAuth] Validation throttled, last validation was too recent");
            return null;
        }

        setIsValidating(true);
        lastValidationTime.current = now;

        try {
            console.log("[useMCPAuth] Validating auth token...");
            const result = await mcpClientService.callTool("validate_token");

            if (!mounted.current) {
                return null;
            }

            if (result.content && Array.isArray(result.content)) {
                const content = result.content[0];
                if (content.type === "text" && typeof content.text === "string") {
                    try {
                        const validation: TokenValidationResult = JSON.parse(content.text);
                        console.log("[useMCPAuth] Token validation result:", validation.success ? "SUCCESS" : "FAILED");

                        // Update state with validation results
                        setIsAuthenticated(validation.success && validation.user !== null);
                        setTokenValidated(validation.success);
                        setValidatedUser(validation.user);
                        setAuthenticatedUserEmail(validation.user?.email || getCurrentUserEmail());

                        return validation;
                    }
                    catch (parseError) {
                        console.error("[useMCPAuth] Failed to parse token validation response:", parseError);
                        setTokenValidated(false);
                        return null;
                    }
                }
            }
            setTokenValidated(false);
            return null;
        }
        catch (error) {
            console.error("[useMCPAuth] Token validation error:", error);
            setTokenValidated(false);
            return null;
        }
        finally {
            if (mounted.current) {
                setIsValidating(false);
            }
        }
    }, [isCindraEnabled, isCindraConnected, hasAuthToken, getCurrentUserEmail, isValidating]);

    const getAuthStatus = useCallback(async (): Promise<{ authenticated: boolean; email?: string; user?: TokenValidationResult["user"] } | null> => {
        if (!isCindraEnabled) {
            return { authenticated: false };
        }

        const validation = await validateToken();

        if (validation?.success && validation.user) {
            return {
                authenticated: true,
                email: validation.user.email,
                user: validation.user
            };
        }

        return { authenticated: false };
    }, [isCindraEnabled, validateToken]);

    // Effect for handling initial state and connection changes
    useEffect(() => {
        mounted.current = true;

        const initializeAuthState = () => {
            if (!mounted.current) return;

            // If Cindra is disabled, set appropriate state
            if (!isCindraEnabled) {
                setIsAuthenticated(false);
                setTokenValidated(null);
                setValidatedUser(null);
                setAuthenticatedUserEmail(null);
                return;
            }

            const hasToken = hasAuthToken();
            const userEmail = getCurrentUserEmail();

            // console.log("[useMCPAuth] State change - Connected:", isCindraConnected, "HasToken:", hasToken, "Error:", error);

            // Set the initial state based on session data
            setAuthenticatedUserEmail(userEmail);

            // If no token, clear auth state
            if (!hasToken) {
                console.log("[useMCPAuth] No auth token, clearing state");
                setIsAuthenticated(false);
                setTokenValidated(false);
                setValidatedUser(null);
                setAuthenticatedUserEmail(null);
                return;
            }

            // If we have an error, set failure state
            if (error) {
                console.log("[useMCPAuth] MCP error detected, setting failure state");
                setIsAuthenticated(false);
                setTokenValidated(null);
                return;
            }

            // If we're not connected, set indeterminate state
            if (!isCindraConnected) {
                // console.log("[useMCPAuth] MCP not connected, setting indeterminate state");
                setIsAuthenticated(false);
                setTokenValidated(null);
                return;
            }

            // If we have both connection and token, validate after a delay (throttled)
            if (isCindraConnected && hasToken && !isValidating) {
                // console.log("[useMCPAuth] MCP connected with auth token, scheduling validation...");

                // Clear any existing validation timeout
                if (validationCooldown.current) {
                    clearTimeout(validationCooldown.current);
                }

                // Only validate if we haven't validated recently
                const timeSinceLastValidation = Date.now() - lastValidationTime.current;
                if (timeSinceLastValidation >= VALIDATION_THROTTLE_MS) {
                    validationCooldown.current = setTimeout(async () => {
                        if (mounted.current && !isValidating) {
                            try {
                                await validateToken();
                            }
                            catch (err) {
                                console.error("[useMCPAuth] Auto-validation failed:", err);
                            }
                        }
                    }, 100);
                }
                else {
                    console.log("[useMCPAuth] Skipping validation, too recent");
                }
            }
        };

        initializeAuthState();

        return () => {
            mounted.current = false;
            if (validationCooldown.current) {
                clearTimeout(validationCooldown.current);
                validationCooldown.current = null;
            }
        };
    }, [isCindraEnabled, isCindraConnected, error]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            mounted.current = false;
            if (validationCooldown.current) {
                clearTimeout(validationCooldown.current);
            }
        };
    }, []);

    const value = {
        authenticatedUserEmail,
        getAuthStatus,
        hasAuthToken: hasAuthToken(),
        isCindraAuthenticated: isAuthenticated,
        isCindraEnabled,
        tokenValidated,
        validateToken,
        validatedUser
    };

    return (
        <MCPAuthContext.Provider value={value}>
            {children}
        </MCPAuthContext.Provider>
    );
};

export const useMCPAuth = (): UseMCPAuthResult => {
    const context = useContext(MCPAuthContext);
    if (!context) {
        throw new Error("useMCPAuth must be used within an MCPAuthProvider");
    }
    return context;
};
